import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import client, { apiErrorMessage, apiErrorDetails } from '@/lib/api';
import { stripEmpty } from '@/lib/formUtils';

const EMPTY = {
  name: '', contactPersonName: '', email: '', mobile: '', alternateContactNumber: '', address: '',
  freeTrialEnabled: false, trialStartDate: '', trialEndDate: '',
  gstNumber: '', panNumber: '', companyName: '', city: '', state: '', country: 'India', pincode: '',
};

export default function WarehouseFormDialog({ open, onOpenChange, initial, onSaved }) {
  const isEdit = !!initial;
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      if (initial) {
        setForm({
          ...EMPTY,
          ...initial,
          ...(initial.profile || {}),
          trialStartDate: initial.trialStartDate ? initial.trialStartDate.slice(0, 10) : '',
          trialEndDate: initial.trialEndDate ? initial.trialEndDate.slice(0, 10) : '',
        });
      } else {
        setForm(EMPTY);
      }
      setErrors({});
    }
  }, [open, initial]);

  function update(patch) {
    setForm((f) => ({ ...f, ...patch }));
  }

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    setErrors({});
    try {
      const payload = stripEmpty({ ...form }, { keepFalsy: true });
      // freeTrialEnabled is a boolean; stripEmpty keeps `false` because keepFalsy:true is default.
      // Explicitly ensure the boolean is passed through:
      payload.freeTrialEnabled = !!form.freeTrialEnabled;
      const res = isEdit
        ? await client.patch(`/warehouses/${initial.id}`, payload)
        : await client.post('/warehouses', payload);
      toast.success(isEdit ? 'Warehouse updated' : 'Warehouse created');
      onSaved(res.data.data);
    } catch (err) {
      const details = apiErrorDetails(err);
      if (details && Array.isArray(details)) {
        const map = {};
        for (const d of details) {
          const field = d.field.replace('body.', '');
          map[field] = d.message;
        }
        setErrors(map);
      }
      toast.error(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="warehouse-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{isEdit ? 'Edit warehouse' : 'Create new warehouse'}</DialogTitle>
          <DialogDescription className="text-zinc-500">Warehouse tenant details, GST profile, and free-trial settings.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <Section title="Warehouse details">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Warehouse name*" error={errors.name}>
                <Input value={form.name} onChange={(e) => update({ name: e.target.value })} required data-testid="wh-form-name" />
              </Field>
              <Field label="Contact person*" error={errors.contactPersonName}>
                <Input value={form.contactPersonName} onChange={(e) => update({ contactPersonName: e.target.value })} required data-testid="wh-form-contact" />
              </Field>
              <Field label="Email*" error={errors.email}>
                <Input type="email" value={form.email} onChange={(e) => update({ email: e.target.value })} required disabled={isEdit} data-testid="wh-form-email" />
              </Field>
              <Field label="Mobile*" error={errors.mobile}>
                <Input value={form.mobile} onChange={(e) => update({ mobile: e.target.value })} required data-testid="wh-form-mobile" />
              </Field>
              <Field label="Alternate contact">
                <Input value={form.alternateContactNumber || ''} onChange={(e) => update({ alternateContactNumber: e.target.value })} data-testid="wh-form-alt-contact" />
              </Field>
              <Field label="Company name">
                <Input value={form.companyName || ''} onChange={(e) => update({ companyName: e.target.value })} data-testid="wh-form-company" />
              </Field>
            </div>
            <Field label="Address*" error={errors.address} className="mt-3">
              <Textarea value={form.address} onChange={(e) => update({ address: e.target.value })} required rows={2} data-testid="wh-form-address" />
            </Field>
          </Section>

          <Section title="GST & compliance">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="GST number"><Input value={form.gstNumber || ''} onChange={(e) => update({ gstNumber: e.target.value })} data-testid="wh-form-gst" /></Field>
              <Field label="PAN number"><Input value={form.panNumber || ''} onChange={(e) => update({ panNumber: e.target.value })} data-testid="wh-form-pan" /></Field>
              <Field label="City"><Input value={form.city || ''} onChange={(e) => update({ city: e.target.value })} data-testid="wh-form-city" /></Field>
              <Field label="State"><Input value={form.state || ''} onChange={(e) => update({ state: e.target.value })} data-testid="wh-form-state" /></Field>
              <Field label="Country"><Input value={form.country || ''} onChange={(e) => update({ country: e.target.value })} data-testid="wh-form-country" /></Field>
              <Field label="Pincode"><Input value={form.pincode || ''} onChange={(e) => update({ pincode: e.target.value })} data-testid="wh-form-pincode" /></Field>
            </div>
          </Section>

          <Section title="Free trial">
            <div className="flex items-center gap-3 mb-3">
              <Switch id="wh-trial" checked={!!form.freeTrialEnabled} onCheckedChange={(v) => update({ freeTrialEnabled: v })} data-testid="wh-form-trial-switch" />
              <Label htmlFor="wh-trial" className="text-sm">Enable free trial</Label>
            </div>
            {form.freeTrialEnabled && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Trial start"><Input type="date" value={form.trialStartDate || ''} onChange={(e) => update({ trialStartDate: e.target.value })} data-testid="wh-form-trial-start" /></Field>
                <Field label="Trial end"><Input type="date" value={form.trialEndDate || ''} onChange={(e) => update({ trialEndDate: e.target.value })} data-testid="wh-form-trial-end" /></Field>
              </div>
            )}
          </Section>

          <DialogFooter className="border-t border-zinc-200 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="wh-form-cancel">Cancel</Button>
            <Button type="submit" disabled={loading} className="bg-zinc-950 hover:bg-zinc-800 text-white" data-testid="wh-form-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : isEdit ? 'Save changes' : 'Create warehouse'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }) {
  return (
    <div className="space-y-3">
      <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono">{title}</div>
      {children}
    </div>
  );
}

function Field({ label, error, children, className = '' }) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <Label className="text-xs text-zinc-600">{label}</Label>
      {children}
      {error && <div className="text-xs text-red-600">{error}</div>}
    </div>
  );
}
